import { assertNever, RequiredNotNull } from "@msys/common";
import { BigNumber } from "bignumber.js";
import { findIndex, isNil, merge, omit } from "lodash-es";
import omitDeep from "omit-deep-lodash";
import { v4 } from "uuid";
import {
  DefineItemProps2Entry,
  DefineItemProps2EntryBool,
  DefineItemProps2EntryBoolComputed,
  DefineItemProps2EntryNumber,
  DefineItemProps2EntryNumberArray,
  DefineItemProps2EntryNumberArrayComputed,
  DefineItemProps2EntryNumberComputed,
  DefineItemProps2EntryText,
  DefineItemProps2EntryTextArray,
  DefineItemProps2EntryTextArrayComputed,
  DefineItemProps2EntryTextComputed,
  EnterItemProps2ValueEntry,
  Props2,
  Props2AllowedValuesNumber,
  Props2AllowedValuesText,
  Props2Bool,
  Props2BoolComputed,
  Props2NonComputed,
  Props2Number,
  Props2NumberArray,
  Props2NumberArrayComputed,
  Props2NumberComputed,
  Props2NumberRange,
  Props2Text,
  Props2TextArray,
  Props2TextArrayComputed,
  Props2TextComputed,
} from "../../../clients/graphqlTypes.js";
import { Attachment } from "../attachments/helpers.js";
import { PimPropertyTypeFragment } from "../products/Product.generated.js";
import type { ItemTemplateSearchFilterPropertyFilterFragment } from "../templates/templateSearchFilters.generated.js";
import { ItemPropsOverrideFragment } from "./properties.generated.js";

export type PropType = "TEXT" | "NUMBER" | "BOOLEAN";
export const PROP_VALUES: PropType[] = ["TEXT", "NUMBER", "BOOLEAN"];

export type PropValues =
  | {
      isNumber: true;
      isText: false;
      isBool: false;
      isArray: false;
      valueNumber: number | null;
    }
  | {
      isNumber: true;
      isText: false;
      isBool: false;
      isArray: true;
      valueNumberArray: number[] | null;
    }
  | {
      isNumber: false;
      isText: true;
      isBool: false;
      isArray: false;
      valueText: string | null;
    }
  | {
      isNumber: false;
      isText: true;
      isBool: false;
      isArray: true;
      valueTextArray: string[] | null;
    }
  | {
      isNumber: false;
      isText: false;
      isBool: true;
      isArray: false;
      valueBool: boolean | null;
    };

export type AllowedValue = {
  key: string;
  value: number | string | null;
  media: Attachment | null;
};

export type TemplateTypePropValues = {
  hasDefinedRange: boolean;
  hasDefinedStep: boolean;
  rangeMax: number | null;
  rangeMin: number | null;
  templateTypePropertyType: PropType;
  templateTypeAllowedValues: AllowedValue[];
  propertyExcludedAllowedValues: AllowedValue[];
  propertyType: PropType;
};

export function getPropValues(prop: Props2): PropValues {
  if (isNumberType(prop)) {
    if (!isArrayType(prop)) {
      return {
        isNumber: true,
        isText: false,
        isBool: false,
        isArray: false,
        valueNumber: isNonComputedProp(prop) ? prop.valueNumber || null : null,
      };
    } else {
      return {
        isNumber: true,
        isText: false,
        isBool: false,
        isArray: true,
        valueNumberArray: isNonComputedProp(prop)
          ? prop.valueNumberArray || null
          : null,
      };
    }
  } else if (isTextType(prop)) {
    if (!isArrayType(prop)) {
      return {
        isNumber: false,
        isText: true,
        isBool: false,
        isArray: false,
        valueText: isNonComputedProp(prop) ? prop.valueText || null : null,
      };
    } else {
      return {
        isNumber: false,
        isText: true,
        isBool: false,
        isArray: true,
        valueTextArray: isNonComputedProp(prop)
          ? prop.valueTextArray || null
          : null,
      };
    }
  } else if (isBooleanType(prop)) {
    return {
      isNumber: false,
      isText: false,
      isBool: true,
      isArray: false,
      valueBool: isNonComputedProp(prop) ? prop.valueBool || null : null,
    };
  }
  assertNever(prop, "Wrong prop type");
}

export function getAllowedValues(prop: Props2): AllowedValue[] {
  return isNonComputedProp(prop)
    ? isTextType(prop)
      ? prop.allowedValuesText.map(allowedValueText => ({
          key: v4(),
          media: allowedValueText.media ?? null,
          value: allowedValueText.allowedText,
        }))
      : isNumberType(prop)
        ? prop.allowedValuesNumber.map(allowedValueNumber => ({
            key: v4(),
            media: allowedValueNumber.media ?? null,
            value: allowedValueNumber.allowedNumber,
          }))
        : []
    : [];
}

export function isSameTypeForAllowedValues(
  prop1: Props2,
  prop2: Props2
): boolean {
  return (
    isNonComputedProp(prop1) &&
    isNonComputedProp(prop2) &&
    ((isTextType(prop1) && isTextType(prop2)) ||
      (isNumberType(prop1) && isNumberType(prop2)))
  );
}

export function getTemplateTypePropValues(
  ttProp: Props2,
  prop: Props2
): TemplateTypePropValues {
  const templateTypeCanHaveRangeOrStep =
    isNumberType(ttProp) && isNonComputedProp(ttProp);

  const templateTypeHasDefinedRange =
    templateTypeCanHaveRangeOrStep && Boolean(ttProp.range);

  const templateTypeHasDefinedStep =
    templateTypeCanHaveRangeOrStep && Boolean(ttProp.step);

  const templateTypeRangeMin = templateTypeHasDefinedRange
    ? (ttProp.range?.min ?? null)
    : null;
  const templateTypeRangeMax = templateTypeHasDefinedRange
    ? (ttProp.range?.max ?? null)
    : null;

  const templateTypeAllowedValues = getAllowedValues(ttProp);
  const propertyAllowedValues = getAllowedValues(prop);

  const propertyExcludedAllowedValues: AllowedValue[] =
    isSameTypeForAllowedValues(ttProp, prop)
      ? propertyAllowedValues.filter(
          av => !templateTypeAllowedValues.some(tav => tav.value === av.value)
        )
      : propertyAllowedValues;

  return {
    hasDefinedRange: templateTypeHasDefinedRange,
    hasDefinedStep: templateTypeHasDefinedStep,
    rangeMax: templateTypeRangeMax,
    rangeMin: templateTypeRangeMin,
    templateTypePropertyType: getPropertyType(ttProp),
    templateTypeAllowedValues: templateTypeAllowedValues,
    propertyExcludedAllowedValues: propertyExcludedAllowedValues,
    propertyType: getPropertyType(prop),
  };
}

export function getPropValuesFromType({
  type,
  isArray,
}: TypeFields): PropValues {
  if (type === "NUMBER") {
    if (!isArray) {
      return {
        isNumber: true,
        isText: false,
        isBool: false,
        isArray: false,
        valueNumber: null,
      };
    } else {
      return {
        isNumber: true,
        isText: false,
        isBool: false,
        isArray: true,
        valueNumberArray: null,
      };
    }
  } else if (type === "TEXT") {
    if (!isArray) {
      return {
        isNumber: false,
        isText: true,
        isBool: false,
        isArray: false,
        valueText: null,
      };
    } else {
      return {
        isNumber: false,
        isText: true,
        isBool: false,
        isArray: true,
        valueTextArray: null,
      };
    }
  } else if (type === "BOOLEAN") {
    return {
      isNumber: false,
      isText: false,
      isBool: true,
      isArray: false,
      valueBool: null,
    };
  }
  assertNever(type, "Wrong prop type");
}

export interface TypeFields {
  type: PropType;
  isArray: boolean;
}

export function isNonComputedProp(prop: Props2): prop is Props2NonComputed {
  return !isComputedProp(prop);
}

export function isComputedProp(
  prop: Props2
): prop is Exclude<Props2, Props2NonComputed> {
  switch (prop.__typename) {
    case "Props2Bool":
    case "Props2Text":
    case "Props2TextArray":
    case "Props2NumberArray":
    case "Props2Number": {
      return false;
    }
    case "Props2TextComputed":
    case "Props2NumberComputed":
    case "Props2TextArrayComputed":
    case "Props2NumberArrayComputed":
    case "Props2BoolComputed": {
      return true;
    }
    default: {
      assertNever(prop);
    }
  }
}

export function isPropMissingValue(prop: Props2) {
  switch (prop.__typename) {
    case "Props2TextComputed":
    case "Props2NumberComputed":
    case "Props2TextArrayComputed":
    case "Props2NumberArrayComputed":
    case "Props2BoolComputed": {
      return false;
    }
    case "Props2Bool":
    case "Props2Text":
    case "Props2Number":
    case "Props2TextArray":
    case "Props2NumberArray": {
      return getPropValue(prop) === null;
    }
    default: {
      assertNever(prop);
    }
  }
}

export function isPropHavingRangeDefined(prop: Props2) {
  switch (prop.__typename) {
    case "Props2TextComputed":
    case "Props2NumberComputed":
    case "Props2TextArrayComputed":
    case "Props2NumberArrayComputed":
    case "Props2BoolComputed":
    case "Props2Bool":
    case "Props2Text":
    case "Props2TextArray": {
      return false;
    }
    case "Props2Number":
    case "Props2NumberArray": {
      return (
        (prop.range?.min ?? null) !== null || (prop.range?.max ?? null) !== null
      );
    }
    default: {
      assertNever(prop);
    }
  }
}

export function isPropHavingStepDefined(prop: Props2) {
  switch (prop.__typename) {
    case "Props2TextComputed":
    case "Props2NumberComputed":
    case "Props2TextArrayComputed":
    case "Props2NumberArrayComputed":
    case "Props2BoolComputed":
    case "Props2Bool":
    case "Props2Text":
    case "Props2TextArray": {
      return false;
    }
    case "Props2Number":
    case "Props2NumberArray": {
      return (prop.step ?? null) !== null;
    }
    default: {
      assertNever(prop);
    }
  }
}

export function getPropValue(prop: Props2) {
  switch (prop.__typename) {
    case "Props2Bool": {
      return prop.valueBool;
    }
    case "Props2Number": {
      return prop.valueNumber;
    }
    case "Props2NumberArray": {
      return prop.valueNumberArray;
    }
    case "Props2Text": {
      return prop.valueText;
    }
    case "Props2TextArray": {
      return prop.valueTextArray;
    }
    case "Props2BoolComputed": {
      return prop.valueBoolComputed;
    }
    case "Props2NumberComputed": {
      return prop.valueNumberComputed;
    }
    case "Props2NumberArrayComputed": {
      return prop.valueNumberArrayComputed;
    }
    case "Props2TextComputed": {
      return prop.valueTextComputed;
    }
    case "Props2TextArrayComputed": {
      return prop.valueTextArrayComputed;
    }
    default: {
      assertNever(prop);
    }
  }
}

export function getPropDisplayedValue(
  prop: Props2,
  getBoolLabel: (key: boolean) => string,
  getNumberLabel: (key: number) => string,
  notSetSymbol: string,
  unit: string,
  valueOverride?: any
) {
  switch (prop.__typename) {
    case "Props2Bool": {
      const valueBool = (
        valueOverride !== undefined ? valueOverride : prop.valueBool
      ) as boolean | undefined | null;
      return valueBool !== null && valueBool !== undefined
        ? getBoolLabel(valueBool)
        : notSetSymbol;
    }
    case "Props2Number": {
      const valueNumber = (
        valueOverride !== undefined ? valueOverride : prop.valueNumber
      ) as number | undefined | null;
      return valueNumber !== null && valueNumber !== undefined
        ? `${getNumberLabel(valueNumber)}${unit}`
        : `${notSetSymbol}${notSetSymbol ? unit : ""}`;
    }
    case "Props2NumberArray": {
      const valueNumberArray = (
        valueOverride !== undefined ? valueOverride : prop.valueNumberArray
      ) as number[] | undefined | null;
      return valueNumberArray && valueNumberArray.length > 0
        ? valueNumberArray.map(v => `${getNumberLabel(v)}${unit}`).join(", ")
        : `${notSetSymbol}${notSetSymbol ? unit : ""}`;
    }
    case "Props2Text": {
      const valueText = (
        valueOverride !== undefined ? valueOverride : prop.valueText
      ) as string | undefined | null;
      return valueText !== null && valueText !== undefined && valueText.trim()
        ? valueText.trim()
        : notSetSymbol;
    }
    case "Props2TextArray": {
      const valueTextArray = (
        valueOverride !== undefined ? valueOverride : prop.valueTextArray
      ) as string[] | undefined | null;
      return valueTextArray && valueTextArray.length > 0
        ? valueTextArray.map(v => v.trim()).join(", ")
        : notSetSymbol;
    }
    case "Props2BoolComputed": {
      const valueBool = (
        valueOverride !== undefined ? valueOverride : prop.valueBoolComputed
      ) as boolean | undefined | null;
      return !prop.missingValue && valueBool !== null && valueBool !== undefined
        ? getBoolLabel(valueBool)
        : notSetSymbol;
    }
    case "Props2NumberComputed": {
      const valueNumber = (
        valueOverride !== undefined ? valueOverride : prop.valueNumberComputed
      ) as number | undefined | null;
      return !prop.missingValue &&
        valueNumber !== null &&
        valueNumber !== undefined
        ? `${getNumberLabel(valueNumber)}${unit}`
        : `${notSetSymbol}${notSetSymbol ? unit : ""}`;
    }
    case "Props2NumberArrayComputed": {
      const valueNumberArray = (
        valueOverride !== undefined
          ? valueOverride
          : prop.valueNumberArrayComputed
      ) as number[] | undefined | null;
      return !prop.missingValue &&
        valueNumberArray &&
        valueNumberArray.length > 0
        ? valueNumberArray.map(v => `${getNumberLabel(v)}${unit}`).join(", ")
        : `${notSetSymbol}${notSetSymbol ? unit : ""}`;
    }
    case "Props2TextComputed": {
      const valueText = (
        valueOverride !== undefined ? valueOverride : prop.valueTextComputed
      ) as string | undefined | null;
      return !prop.missingValue &&
        valueText !== null &&
        valueText !== undefined &&
        valueText.trim()
        ? valueText.trim()
        : notSetSymbol;
    }
    case "Props2TextArrayComputed": {
      const valueTextArray = (
        valueOverride !== undefined
          ? valueOverride
          : prop.valueTextArrayComputed
      ) as string[] | undefined | null;
      return !prop.missingValue && valueTextArray && valueTextArray.length > 0
        ? valueTextArray.map(v => v.trim()).join(", ")
        : notSetSymbol;
    }
    default: {
      assertNever(prop);
    }
  }
}

export function getPropAlternativeAllowedValues(
  prop: Props2,
  getNumberLabel: (key: number) => string,
  unit: string
) {
  switch (prop.__typename) {
    case "Props2Bool":
      return "";
    case "Props2Number":
      return (
        prop.allowedValuesNumber && prop.allowedValuesNumber.length > 0
          ? prop.allowedValuesNumber
              .filter(av => av.allowedNumber !== prop.valueNumber)
              .map(av => `${getNumberLabel(av.allowedNumber)}${unit}`)
          : []
      ).join(", ");
    case "Props2NumberArray":
      return (
        prop.allowedValuesNumber && prop.allowedValuesNumber.length > 0
          ? prop.allowedValuesNumber
              .filter(av => !prop.valueNumberArray?.includes(av.allowedNumber))
              .map(av => `${getNumberLabel(av.allowedNumber)}${unit}`)
          : []
      ).join(", ");
    case "Props2Text":
      return (
        prop.allowedValuesText && prop.allowedValuesText.length > 0
          ? prop.allowedValuesText
              .filter(av => av.allowedText !== prop.valueText)
              .map(av => av.allowedText.trim())
          : []
      ).join(", ");
    case "Props2TextArray":
      return (
        prop.allowedValuesText && prop.allowedValuesText.length > 0
          ? prop.allowedValuesText
              .filter(av => !prop.valueTextArray?.includes(av.allowedText))
              .map(av => av.allowedText.trim())
          : []
      ).join(", ");
    case "Props2BoolComputed":
      return "";
    case "Props2NumberComputed":
      return "";
    case "Props2NumberArrayComputed":
      return "";
    case "Props2TextComputed":
      return "";
    case "Props2TextArrayComputed":
      return "";
    default: {
      assertNever(prop);
    }
  }
}

export function getPropertyUnit(prop: Props2) {
  switch (prop.__typename) {
    case "Props2Bool":
    case "Props2Text":
    case "Props2TextArray":
    case "Props2BoolComputed":
    case "Props2TextComputed":
    case "Props2TextArrayComputed":
      return null;
    case "Props2Number":
    case "Props2NumberArray":
    case "Props2NumberComputed":
    case "Props2NumberArrayComputed":
      return prop.unit;
    default: {
      assertNever(prop);
    }
  }
}

export function getPropertyDisplayedUnit(
  prop: Props2,
  getUnitLabel: (key: string) => string
) {
  switch (prop.__typename) {
    case "Props2Bool":
    case "Props2Text":
    case "Props2TextArray":
    case "Props2BoolComputed":
    case "Props2TextComputed":
    case "Props2TextArrayComputed":
      return "";
    case "Props2Number":
    case "Props2NumberArray":
    case "Props2NumberComputed":
    case "Props2NumberArrayComputed": {
      const label = prop.unit ? getUnitLabel(prop.unit) : "";
      if (label.length > 3) return ` ${label}`;
      return label;
    }
    default: {
      assertNever(prop);
    }
  }
}

export function getPropValueWithUnit(
  prop: Props2,
  getUnitLabel: (key: string) => string,
  getBoolLabel: (key: boolean) => string,
  getNumberLabel: (key: number) => string,
  notSetSymbol: string
) {
  const unit = getPropertyDisplayedUnit(prop, getUnitLabel);
  return getPropDisplayedValue(
    prop,
    getBoolLabel,
    getNumberLabel,
    notSetSymbol,
    unit
  );
}

export function getPropValueWithUnitAndAllowedValues(
  prop: Props2,
  getUnitLabel: (key: string) => string,
  getBoolLabel: (key: boolean) => string,
  getNumberLabel: (key: number) => string,
  notSetSymbol: string
) {
  const unit = getPropertyDisplayedUnit(prop, getUnitLabel);
  const displayedValue = getPropDisplayedValue(
    prop,
    getBoolLabel,
    getNumberLabel,
    notSetSymbol,
    unit
  );
  const alternativeAllowedValues = getPropAlternativeAllowedValues(
    prop,
    getNumberLabel,
    unit
  );
  return { displayedValue, alternativeAllowedValues, unit };
}

export function stringArrayToNumberArray(stringArray: string[]): number[] {
  const numberArray = stringArray.map(parseFloat);
  if (numberArray.some(isNaN)) throw new Error("Failed to parse float");

  return numberArray;
}

export function propToDefineInput(
  property: Props2Bool
): RequiredNotNull<Pick<DefineItemProps2Entry, "bool">>;
export function propToDefineInput(
  property: Props2BoolComputed
): RequiredNotNull<Pick<DefineItemProps2Entry, "boolComputed">>;
export function propToDefineInput(
  property: Props2Number
): RequiredNotNull<Pick<DefineItemProps2Entry, "number">>;
export function propToDefineInput(
  property: Props2NumberComputed
): RequiredNotNull<Pick<DefineItemProps2Entry, "numberComputed">>;
export function propToDefineInput(
  property: Props2NumberArray
): RequiredNotNull<Pick<DefineItemProps2Entry, "numberArray">>;
export function propToDefineInput(
  property: Props2NumberArrayComputed
): RequiredNotNull<Pick<DefineItemProps2Entry, "numberArrayComputed">>;
export function propToDefineInput(
  property: Props2Text
): RequiredNotNull<Pick<DefineItemProps2Entry, "text">>;
export function propToDefineInput(
  property: Props2TextComputed
): RequiredNotNull<Pick<DefineItemProps2Entry, "textComputed">>;
export function propToDefineInput(
  property: Props2TextArray
): RequiredNotNull<Pick<DefineItemProps2Entry, "textArray">>;
export function propToDefineInput(
  property: Props2TextArrayComputed
): RequiredNotNull<Pick<DefineItemProps2Entry, "textArrayComputed">>;
export function propToDefineInput(
  property: Props2NonComputed
):
  | RequiredNotNull<Pick<DefineItemProps2Entry, "bool">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "number">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "numberArray">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "text">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "textArray">>;
export function propToDefineInput(
  property: Exclude<Props2, Props2NonComputed>
):
  | RequiredNotNull<Pick<DefineItemProps2Entry, "boolComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "numberComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "numberArrayComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "textComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "textArrayComputed">>;
export function propToDefineInput(property: Props2): DefineItemProps2Entry;
export function propToDefineInput(property: Props2): DefineItemProps2Entry {
  switch (property.__typename) {
    case "Props2Bool": {
      const bool = omitDeep(property, [
        "__typename",
      ]) as unknown as DefineItemProps2EntryBool;
      if (bool.valuesMediaBool)
        bool.valuesMediaBool = bool.valuesMediaBool
          .filter(v => v.media)
          .map(v => ({ ...v, media: omit(v.media!, "id") }));
      return { bool };
    }
    case "Props2Number": {
      const number = omitDeep(property, [
        "__typename",
      ]) as unknown as DefineItemProps2EntryNumber;
      if (number.allowedValuesNumber)
        number.allowedValuesNumber = number.allowedValuesNumber.map(v =>
          v.media ? { ...v, media: omit(v.media, "id") } : v
        );
      return { number };
    }
    case "Props2NumberArray": {
      const numberArray = omitDeep(property, [
        "__typename",
      ]) as unknown as DefineItemProps2EntryNumberArray;
      if (numberArray.allowedValuesNumber)
        numberArray.allowedValuesNumber = numberArray.allowedValuesNumber.map(
          v => (v.media ? { ...v, media: omit(v.media, "id") } : v)
        );
      return { numberArray };
    }
    case "Props2Text": {
      const text = omitDeep(property, [
        "__typename",
      ]) as unknown as DefineItemProps2EntryText;
      if (text.allowedValuesText)
        text.allowedValuesText = text.allowedValuesText.map(v =>
          v.media ? { ...v, media: omit(v.media, "id") } : v
        );
      return { text };
    }
    case "Props2TextArray": {
      const textArray = omitDeep(property, [
        "__typename",
      ]) as unknown as DefineItemProps2EntryTextArray;
      if (textArray.allowedValuesText)
        textArray.allowedValuesText = textArray.allowedValuesText.map(v =>
          v.media ? { ...v, media: omit(v.media, "id") } : v
        );
      return { textArray };
    }
    case "Props2BoolComputed":
    case "Props2NumberComputed":
    case "Props2NumberArrayComputed":
    case "Props2TextComputed":
    case "Props2TextArrayComputed":
      return computedPropToDefineInput(property);
    default: {
      assertNever(property);
    }
  }
}

export function computedPropToDefineInput(
  property: Exclude<Props2, Props2NonComputed>,
  newExpression?: string
):
  | RequiredNotNull<Pick<DefineItemProps2Entry, "boolComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "numberComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "numberArrayComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "textComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "textArrayComputed">> {
  switch (property.__typename) {
    case "Props2BoolComputed": {
      return {
        boolComputed: merge(
          omit(property, [
            "__typename",
            "valueBoolComputed",
            "missingValue",
            "askIfExpr",
          ]),
          { expr: newExpression }
        ) as unknown as DefineItemProps2EntryBoolComputed,
      };
    }

    case "Props2NumberComputed": {
      return {
        numberComputed: merge(
          omit(property, [
            "__typename",
            "valueNumberComputed",
            "missingValue",
            "askIfExpr",
          ]),
          { expr: newExpression }
        ) as unknown as DefineItemProps2EntryNumberComputed,
      };
    }

    case "Props2NumberArrayComputed": {
      return {
        numberArrayComputed: merge(
          omit(property, [
            "__typename",
            "valueNumberArrayComputed",
            "missingValue",
            "askIfExpr",
          ]),
          { expr: newExpression }
        ) as unknown as DefineItemProps2EntryNumberArrayComputed,
      };
    }

    case "Props2TextComputed": {
      return {
        textComputed: merge(
          omit(property, [
            "__typename",
            "valueTextComputed",
            "missingValue",
            "askIfExpr",
          ]),
          { expr: newExpression }
        ) as unknown as DefineItemProps2EntryTextComputed,
      };
    }

    case "Props2TextArrayComputed": {
      return {
        textArrayComputed: merge(
          omit(property, [
            "__typename",
            "valueTextArrayComputed",
            "missingValue",
            "askIfExpr",
          ]),
          { expr: newExpression }
        ) as unknown as DefineItemProps2EntryTextArrayComputed,
      };
    }

    default: {
      assertNever(property);
    }
  }
}

export function getPropInputEntryKey(entry: DefineItemProps2Entry) {
  if (entry.bool) return entry.bool.key;
  if (entry.boolComputed) return entry.boolComputed.key;
  if (entry.number) return entry.number.key;
  if (entry.numberComputed) return entry.numberComputed.key;
  if (entry.numberArray) return entry.numberArray.key;
  if (entry.numberArrayComputed) return entry.numberArrayComputed.key;
  if (entry.text) return entry.text.key;
  if (entry.textComputed) return entry.textComputed.key;
  if (entry.textArray) return entry.textArray.key;
  if (entry.textArrayComputed) return entry.textArrayComputed.key;
  throw new Error("Unexpected entry type: " + JSON.stringify(entry));
}

export function getPropInputGroup(entry: DefineItemProps2Entry) {
  if (entry.bool) return entry.bool.group;
  if (entry.boolComputed) return entry.boolComputed.group;
  if (entry.number) return entry.number.group;
  if (entry.numberComputed) return entry.numberComputed.group;
  if (entry.numberArray) return entry.numberArray.group;
  if (entry.numberArrayComputed) return entry.numberArrayComputed.group;
  if (entry.text) return entry.text.group;
  if (entry.textComputed) return entry.textComputed.group;
  if (entry.textArray) return entry.textArray.group;
  if (entry.textArrayComputed) return entry.textArrayComputed.group;
  throw new Error("Unexpected entry type: " + JSON.stringify(entry));
}

export function convertToComputedInput(
  property: Props2Bool,
  expression: string
): RequiredNotNull<Pick<DefineItemProps2Entry, "boolComputed">>;
export function convertToComputedInput(
  property: Props2Number,
  expression: string
): RequiredNotNull<Pick<DefineItemProps2Entry, "numberComputed">>;
export function convertToComputedInput(
  property: Props2NumberArray,
  expression: string
): RequiredNotNull<Pick<DefineItemProps2Entry, "numberArrayComputed">>;
export function convertToComputedInput(
  property: Props2Text,
  expression: string
): RequiredNotNull<Pick<DefineItemProps2Entry, "textComputed">>;
export function convertToComputedInput(
  property: Props2TextArray,
  expression: string
): RequiredNotNull<Pick<DefineItemProps2Entry, "textArrayComputed">>;
export function convertToComputedInput(
  property: Props2NonComputed,
  expression: string
):
  | RequiredNotNull<Pick<DefineItemProps2Entry, "boolComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "numberComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "numberArrayComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "textComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "textArrayComputed">>;
export function convertToComputedInput(
  property: Props2NonComputed,
  expression: string
):
  | RequiredNotNull<Pick<DefineItemProps2Entry, "boolComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "numberComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "numberArrayComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "textComputed">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "textArrayComputed">> {
  const entry = {
    key: property.key,
    label: property.label,
    group: property.group,
    expr: expression,
    clientVisibility: property.clientVisibility,
  };

  switch (property.__typename) {
    case "Props2Bool":
      return { boolComputed: entry };
    case "Props2Number":
      return {
        numberComputed: { ...entry, unit: property.unit },
      };
    case "Props2NumberArray":
      return {
        numberArrayComputed: { ...entry, unit: property.unit },
      };
    case "Props2Text":
      return {
        textComputed: entry,
      };
    case "Props2TextArray":
      return {
        textArrayComputed: entry,
      };
    default:
      assertNever(property);
  }
}

export function convertToNonComputedInput(
  property: Props2BoolComputed
): RequiredNotNull<Pick<DefineItemProps2Entry, "bool">>;
export function convertToNonComputedInput(
  property: Props2NumberComputed
): RequiredNotNull<Pick<DefineItemProps2Entry, "number">>;
export function convertToNonComputedInput(
  property: Props2NumberArrayComputed
): RequiredNotNull<Pick<DefineItemProps2Entry, "numberArray">>;
export function convertToNonComputedInput(
  property: Props2TextComputed
): RequiredNotNull<Pick<DefineItemProps2Entry, "text">>;
export function convertToNonComputedInput(
  property: Props2TextArrayComputed
): RequiredNotNull<Pick<DefineItemProps2Entry, "text">>;
export function convertToNonComputedInput(
  property: Exclude<Props2, Props2NonComputed>
):
  | RequiredNotNull<Pick<DefineItemProps2Entry, "bool">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "number">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "numberArray">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "text">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "textArray">>;
export function convertToNonComputedInput(
  property: Exclude<Props2, Props2NonComputed>
):
  | RequiredNotNull<Pick<DefineItemProps2Entry, "bool">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "number">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "numberArray">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "text">>
  | RequiredNotNull<Pick<DefineItemProps2Entry, "textArray">> {
  const entry = {
    key: property.key,
    label: property.label,
    group: property.group,
    essential: false,
    clientVisibility: property.clientVisibility,
    prompt: "",
    questionControl: [],
    askIfExpr: "",
    customizable: false,
  };

  switch (property.__typename) {
    case "Props2BoolComputed":
      return {
        bool: {
          ...entry,
          valuesMediaBool: [],
          valueBool: property.missingValue ? null : property.valueBoolComputed,
        },
      };
    case "Props2NumberComputed":
      return {
        number: {
          ...entry,
          allowedValuesNumber: [],
          valueNumber: property.missingValue
            ? null
            : property.valueNumberComputed,
          unit: property.unit,
        },
      };
    case "Props2NumberArrayComputed":
      return {
        numberArray: {
          ...entry,
          allowedValuesNumber: [],
          valueNumberArray: property.missingValue
            ? null
            : property.valueNumberArrayComputed,
          unit: property.unit,
        },
      };
    case "Props2TextComputed":
      return {
        text: {
          ...entry,
          allowedValuesText: [],
          valueText: property.missingValue ? null : property.valueTextComputed,
        },
      };
    case "Props2TextArrayComputed":
      return {
        textArray: {
          ...entry,
          allowedValuesText: [],
          valueTextArray: property.missingValue
            ? null
            : property.valueTextArrayComputed,
        },
      };
    default:
      assertNever(property);
  }
}

export function getEnhancedPimPropertyTypeLabel(
  pimProperty: PimPropertyTypeFragment
) {
  return `${pimProperty.label} ${getPimPropertyTypeLabelTypeSuffix(pimProperty)}`.trim();
}

function getPimPropertyTypeLabelTypeSuffix(
  pimProperty: PimPropertyTypeFragment
) {
  switch (pimProperty.kind) {
    case "bool":
      return "(t/f)";
    case "number":
    case "range":
      return "(#)";
    case "text":
      return "(Aa)";
    default:
      assertNever(pimProperty.kind);
  }
}

export function getEnhancedPropertyLabel(
  property: Props2,
  includeRequired: boolean = true
) {
  return `${property.label} ${getPropertyLabelTypeSuffix(property)} ${
    includeRequired ? getPropertyLabelRequiredSuffix(property) : ""
  }`.trim();
}

function getPropertyLabelTypeSuffix(property: Props2) {
  switch (property.__typename) {
    case "Props2Bool":
    case "Props2BoolComputed":
      return "(t/f)";
    case "Props2Number":
    case "Props2NumberComputed":
    case "Props2NumberArray":
    case "Props2NumberArrayComputed":
      return "(#)";
    case "Props2Text":
    case "Props2TextComputed":
    case "Props2TextArray":
    case "Props2TextArrayComputed":
      return "(Aa)";
    default:
      assertNever(property);
  }
}

function getPropertyLabelRequiredSuffix(property: Props2) {
  switch (property.__typename) {
    case "Props2BoolComputed":
    case "Props2NumberComputed":
    case "Props2NumberArrayComputed":
    case "Props2TextComputed":
    case "Props2TextArrayComputed":
      return "";
    case "Props2Bool":
    case "Props2Number":
    case "Props2NumberArray":
    case "Props2Text":
    case "Props2TextArray":
      // return property.essential ? "*" : "";
      return property.questionControl.length > 0 &&
        property.questionControl[0].askWhom.length > 0
        ? "*"
        : "";
    default:
      assertNever(property);
  }
}

export function getPropertyType(prop: Props2): PropType {
  switch (prop.__typename) {
    case "Props2Bool":
    case "Props2BoolComputed":
      return "BOOLEAN";
    case "Props2Number":
    case "Props2NumberComputed":
    case "Props2NumberArray":
    case "Props2NumberArrayComputed":
      return "NUMBER";
    case "Props2Text":
    case "Props2TextComputed":
    case "Props2TextArray":
    case "Props2TextArrayComputed":
      return "TEXT";
    default:
      console.log(prop);
      assertNever(prop);
  }
}

export function isBooleanType(
  prop: Props2
): prop is Props2Bool | Props2BoolComputed {
  switch (prop.__typename) {
    case "Props2Bool":
    case "Props2BoolComputed":
      return true;
    case "Props2Number":
    case "Props2NumberComputed":
    case "Props2NumberArray":
    case "Props2NumberArrayComputed":
    case "Props2Text":
    case "Props2TextComputed":
    case "Props2TextArray":
    case "Props2TextArrayComputed":
      return false;
    default:
      assertNever(prop);
  }
}

export function isNumberType(
  prop: Props2
): prop is
  | Props2Number
  | Props2NumberComputed
  | Props2NumberArray
  | Props2NumberArrayComputed {
  switch (prop.__typename) {
    case "Props2Number":
    case "Props2NumberComputed":
    case "Props2NumberArray":
    case "Props2NumberArrayComputed":
      return true;
    case "Props2Bool":
    case "Props2BoolComputed":
    case "Props2Text":
    case "Props2TextComputed":
    case "Props2TextArray":
    case "Props2TextArrayComputed":
      return false;
    default:
      assertNever(prop);
  }
}

export function isTextType(
  prop: Props2
): prop is
  | Props2Text
  | Props2TextComputed
  | Props2TextArray
  | Props2TextArrayComputed {
  switch (prop.__typename) {
    case "Props2Text":
    case "Props2TextComputed":
    case "Props2TextArray":
    case "Props2TextArrayComputed":
      return true;
    case "Props2Bool":
    case "Props2BoolComputed":
    case "Props2Number":
    case "Props2NumberComputed":
    case "Props2NumberArray":
    case "Props2NumberArrayComputed":
      return false;
    default:
      assertNever(prop);
  }
}

export function isValueNumberInAllowedValuesNumber(
  value: number,
  allowedValues: Props2AllowedValuesNumber[]
): boolean {
  return allowedValues.some(e => e.allowedNumber === value);
}

export function isValueTextInAllowedValuesText(
  value: string,
  allowedValues: Props2AllowedValuesText[]
): boolean {
  return allowedValues.some(e => e.allowedText === value);
}

export function isValueNumberInNumberRange(
  value: number,
  range: Props2NumberRange
): boolean {
  if (isNil(range.min) && isNil(range.max)) {
    // range not defined
    return false;
  }

  if (!isNil(range.min) && value < range.min) {
    return false;
  }

  if (!isNil(range.max) && value > range.max) {
    return false;
  }

  return true;
}

export function isValueNumberInNumberRangeStep(
  value: number,
  range: Props2NumberRange,
  step: number
): boolean {
  if (isNil(range.min) && isNil(range.max)) {
    // range not defined
    return false;
  }

  if (!isNil(range.min)) {
    const multiplier = new BigNumber(value).minus(range.min).dividedBy(step);
    if (!multiplier.isInteger() || multiplier.lt(0)) {
      return false;
    }
  }

  if (isNil(range.min) && !isNil(range.max)) {
    const multiplier = new BigNumber(range.max).minus(value).dividedBy(step);
    if (!multiplier.isInteger() || multiplier.lt(0)) {
      return false;
    }
  }

  return true;
}

/**
 * The nearest value the range and step accept, by the same rules as
 * `isValueNumberInNumberRange` and `isValueNumberInNumberRangeStep`: the step
 * grid is anchored at `min`, or at `max` when there is no `min`.
 */
export function fitValueNumberToRangeStep(
  value: number,
  range: Props2NumberRange | null | undefined,
  step: number | null | undefined
): number {
  if (!range || (isNil(range.min) && isNil(range.max))) return value;

  const min = range.min ?? null;
  const max = range.max ?? null;
  const clamped = Math.min(Math.max(value, min ?? -Infinity), max ?? Infinity);

  const anchor = min ?? max;
  if (isNil(step) || step <= 0 || anchor === null) return clamped;

  const fitted = new BigNumber(clamped)
    .minus(anchor)
    .dividedBy(step)
    .integerValue(BigNumber.ROUND_HALF_UP)
    .times(step)
    .plus(anchor);

  return (
    max !== null && fitted.gt(max) ? fitted.minus(step) : fitted
  ).toNumber();
}

export function isArrayType(
  prop: Props2
): prop is
  | Props2NumberArray
  | Props2NumberArrayComputed
  | Props2TextArray
  | Props2TextArrayComputed {
  switch (prop.__typename) {
    case "Props2NumberArray":
    case "Props2NumberArrayComputed":
    case "Props2TextArray":
    case "Props2TextArrayComputed":
      return true;
    case "Props2Bool":
    case "Props2BoolComputed":
    case "Props2Number":
    case "Props2NumberComputed":
    case "Props2Text":
    case "Props2TextComputed":
      return false;
    default:
      assertNever(prop);
  }
}

export function isNotArrayType(
  prop: Props2
): prop is
  | Props2Bool
  | Props2BoolComputed
  | Props2Number
  | Props2NumberComputed
  | Props2Text
  | Props2TextComputed {
  return !isArrayType(prop);
}

export function nonComputedPropToEntry(
  prop: Props2NonComputed
): EnterItemProps2ValueEntry {
  switch (prop.__typename) {
    case "Props2Bool":
      return { bool: { key: prop.key, valueBool: prop.valueBool } };
    case "Props2Text": {
      const value = prop.valueText?.length ? prop.valueText : null;
      return {
        text: {
          key: prop.key,
          valueText:
            value !== null && prop.allowedValuesText.length > 0
              ? (prop.allowedValuesText.find(
                  ({ allowedText }) => allowedText === value
                )?.allowedText ?? null)
              : value,
        },
      };
    }
    case "Props2Number": {
      const value = prop.valueNumber;
      return {
        number: {
          key: prop.key,
          valueNumber:
            value !== null && prop.allowedValuesNumber.length > 0
              ? (prop.allowedValuesNumber.find(
                  ({ allowedNumber }) => allowedNumber === value
                )?.allowedNumber ?? null)
              : value,
        },
      };
    }
    case "Props2TextArray": {
      const value = prop.valueTextArray;
      return {
        textArray: {
          key: prop.key,
          valueTextArray:
            value?.length && prop.allowedValuesText.length > 0
              ? value.filter(v =>
                  prop.allowedValuesText.some(
                    ({ allowedText }) => allowedText === v
                  )
                )
              : value,
        },
      };
    }
    case "Props2NumberArray": {
      const value = prop.valueNumberArray;
      return {
        numberArray: {
          key: prop.key,
          valueNumberArray:
            value?.length && prop.allowedValuesNumber.length > 0
              ? value.filter(v =>
                  prop.allowedValuesNumber.some(
                    ({ allowedNumber }) => allowedNumber === v
                  )
                )
              : value,
        },
      };
    }
    default:
      assertNever(prop);
  }
}

export type Props2Group<P extends Props2 = Props2> = {
  key: string;
  properties: P[];
};

export function groupProperties<P extends Props2>(
  properties: P[]
): Props2Group<P>[] {
  return properties.reduce((acc, prop) => {
    const groupIndex = findIndex(acc, g => g.key === prop.group);
    if (groupIndex >= 0) {
      acc[groupIndex].properties.push(prop);
    } else {
      acc.push({ key: prop.group, properties: [prop] });
    }
    return acc;
  }, [] as Props2Group<P>[]);
}

export function ungroupProperties<P extends Props2>(
  groups: Props2Group<P>[]
): P[] {
  return groups.map(g => g.properties).flat(1);
}

export function hasPropOverride(override: ItemPropsOverrideFragment) {
  return Boolean(
    !isNil(override.valueAsJson) ||
    !isNil(override.clientVisibility) ||
    override.allowedValuesExclude.length > 0 ||
    override.allowedValuesMedia.length > 0 ||
    override.questionControl
  );
}

export function setPropValue<P extends Props2>(prop: P, value: unknown): P {
  switch (prop.__typename) {
    case "Props2Bool":
      return { ...prop, valueBool: value as boolean | undefined };
    case "Props2Number":
      return { ...prop, valueNumber: value as number | undefined };
    case "Props2NumberArray":
      return { ...prop, valueNumberArray: value as number[] | undefined };
    case "Props2Text":
      return { ...prop, valueText: value as string | undefined };
    case "Props2TextArray":
      return { ...prop, valueTextArray: value as string[] | undefined };
    case "Props2BoolComputed":
      return { ...prop, valueBoolComputed: value as boolean | undefined };
    case "Props2NumberComputed":
      return { ...prop, valueNumberComputed: value as number | undefined };
    case "Props2NumberArrayComputed":
      return {
        ...prop,
        valueNumberArrayComputed: value as number[] | undefined,
      };
    case "Props2TextComputed":
      return { ...prop, valueTextComputed: value as string | undefined };
    case "Props2TextArrayComputed":
      return { ...prop, valueTextArrayComputed: value as string[] | undefined };
    default:
      assertNever(prop);
  }
}

export function setPropAllowedValues<P extends Props2>(
  prop: P,
  filterFn: (
    allowedValue: Props2AllowedValuesNumber | Props2AllowedValuesText
  ) => boolean
): P {
  switch (prop.__typename) {
    case "Props2Bool":
      return prop;
    case "Props2Number":
      return {
        ...prop,
        allowedValuesNumber: prop.allowedValuesNumber.filter(filterFn),
      };
    case "Props2NumberArray":
      return {
        ...prop,
        allowedValuesNumber: prop.allowedValuesNumber.filter(filterFn),
      };
    case "Props2Text": {
      return {
        ...prop,
        allowedValuesText: prop.allowedValuesText.filter(filterFn),
      };
    }
    case "Props2TextArray": {
      return {
        ...prop,
        allowedValuesText: prop.allowedValuesText.filter(filterFn),
      };
    }
    case "Props2BoolComputed":
    case "Props2NumberComputed":
    case "Props2NumberArrayComputed":
    case "Props2TextComputed":
    case "Props2TextArrayComputed":
      return prop;
    default:
      assertNever(prop);
  }
}

export function getAllowedValue(
  allowedValue: Props2AllowedValuesNumber | Props2AllowedValuesText
): string | number {
  if ("allowedText" in allowedValue) return allowedValue.allowedText;
  if ("allowedNumber" in allowedValue) return allowedValue.allowedNumber;
  throw new Error("Wrong allowed value provided");
}

export function applyPropertyOverrides<P extends Props2>(
  prop: P,
  defaultValue:
    | string
    | string[]
    | number
    | number[]
    | boolean
    | null
    | undefined,
  allowedValuesExclude: string[] | number[]
): P {
  let result = prop;
  if (defaultValue !== undefined) {
    result = setPropValue(result, defaultValue);
  }
  if (allowedValuesExclude.length > 0) {
    result = setPropAllowedValues(
      result,
      allowedValue =>
        !(allowedValuesExclude as (string | number)[]).includes(
          getAllowedValue(allowedValue)
        )
    );
  }
  return result;
}

export const getEnhancedTemplateSearchFilterPropLabel = (
  prop: ItemTemplateSearchFilterPropertyFilterFragment
) => `${prop.key} ${getTemplateSearchFilterPropSuffix(prop)}`.trim();

function getTemplateSearchFilterPropSuffix(
  property: ItemTemplateSearchFilterPropertyFilterFragment
) {
  // The suffix names the property's data type, which both flavours of a clause
  // share.
  switch (property.__typename) {
    case "EntitySearchPropertyFilterBoolFilter":
    case "EntitySearchPropertyFilterBoolFilterComputed":
    case "EntitySearchPropertyFilterBoolInFilter":
    case "EntitySearchPropertyFilterBoolInFilterComputed":
      return "(t/f)";
    case "EntitySearchPropertyFilterNumberArrayOfFilter":
    case "EntitySearchPropertyFilterNumberArrayOfFilterComputed":
    case "EntitySearchPropertyFilterNumberBetweenFilter":
    case "EntitySearchPropertyFilterNumberBetweenFilterComputed":
    case "EntitySearchPropertyFilterNumberFilter":
    case "EntitySearchPropertyFilterNumberFilterComputed":
    case "EntitySearchPropertyFilterNumberInFilter":
    case "EntitySearchPropertyFilterNumberInFilterComputed":
      return "(#)";
    case "EntitySearchPropertyFilterTextArrayOfFilter":
    case "EntitySearchPropertyFilterTextArrayOfFilterComputed":
    case "EntitySearchPropertyFilterTextFilter":
    case "EntitySearchPropertyFilterTextFilterComputed":
    case "EntitySearchPropertyFilterTextInFilter":
    case "EntitySearchPropertyFilterTextInFilterComputed":
      return "(Aa)";
    default:
      return assertNever(property);
  }
}

export function propToTemplateSearchFilterProp<P extends Props2NonComputed>(
  prop: P
): ItemTemplateSearchFilterPropertyFilterFragment {
  return prop.__typename === "Props2Bool"
    ? {
        __typename: "EntitySearchPropertyFilterBoolFilterComputed",
        key: prop.key,
        expr: "",
        missingValue: true,
        operatorBool: "eq",
        valueBoolComputed: null,
      }
    : prop.__typename === "Props2Number"
      ? {
          __typename: "EntitySearchPropertyFilterNumberFilterComputed",
          key: prop.key,
          expr: "",
          missingValue: true,
          operatorNumber: "eq",
          valueNumberComputed: null,
        }
      : prop.__typename === "Props2NumberArray"
        ? {
            __typename: "EntitySearchPropertyFilterNumberArrayOfFilterComputed",
            key: prop.key,
            expr: "",
            missingValue: true,
            operatorNumberArrayOf: "allOf",
            valueNumberArrayOfComputed: null,
          }
        : prop.__typename === "Props2Text"
          ? {
              __typename: "EntitySearchPropertyFilterTextFilterComputed",
              key: prop.key,
              expr: "",
              missingValue: true,
              operatorText: "eq",
              valueTextComputed: null,
            }
          : prop.__typename === "Props2TextArray"
            ? {
                __typename:
                  "EntitySearchPropertyFilterTextArrayOfFilterComputed",
                key: prop.key,
                expr: "",
                missingValue: true,
                operatorTextArrayOf: "allOf",
                valueTextArrayOfComputed: null,
              }
            : assertNever(prop);
}
